import {isoDate} from '@/entities/attendance/period';
import type {AttendanceDayRow} from '@/entities/attendance/types';
import type {AccessLogRecord} from '@/entities/log/types';

/**
 * Опоздание рядом со строкой журнала.
 *
 * ## Откуда оно берётся
 *
 * Не отсюда. Журнал (`GET /control/logs`) — это сырые проходы: кто, когда, на каком
 * терминале. Вердикт «опоздал» знает только движок явки, потому что он один держит
 * график: во сколько открывается смена, сколько минут прощается, какие дни рабочие.
 * Поэтому за вердиктом ходят в отчёт (`GET /control/attendance?include_days=true`) и
 * сводят его с лентой здесь.
 *
 * Считать опоздание на клиенте, взяв время открытия отдела, было бы дешевле и
 * НЕВЕРНО: у человека может быть свой график поверх отдельского, у дня — свой признак
 * рабочего, а у компании — свой порог прощения. Две реализации одного правила
 * разъезжаются в первый же спорный день, и спорят как раз о зарплате.
 *
 * ## Почему помечается только ПРИХОД
 *
 * Опоздание — свойство ДНЯ, а не каждого прохода в нём. У человека за день шесть
 * событий; напечатать «+12 мин» на всех шести значит сказать, что он опоздал шесть
 * раз. Помечается ровно то событие, которое движок засчитал приходом
 * (`arrival_time_millis`), остальные — прочерк.
 *
 * ## Чего здесь нет
 *
 * Догадок. Нет графика, день не рабочий, человек не опознан, отчёт не отдался по
 * правам — возвращается `none`, и столбец печатает прочерк. Выдуманная минута
 * опоздания дороже пустой клетки.
 */

/** Что журнал может сказать про одно событие. */
export type EventLateness =
  | {kind: 'none'}
  | {kind: 'onTime'}
  | {kind: 'late'; minutes: number};

const NONE: EventLateness = {kind: 'none'};

/** Дни явки, разложенные по «кто + когда». */
export type LatenessIndex = Map<string, AttendanceDayRow>;

/**
 * Насколько время события должно совпасть с приходом, чтобы считаться им.
 *
 * Движок эхом возвращает миллисекунды самого события, так что совпадение точное;
 * секунда запаса — на случай, если однажды он начнёт округлять. Двух проходов одного
 * человека в одну секунду не бывает, так что подцепить соседнее событие этим нельзя.
 */
const ARRIVAL_TOLERANCE_MS = 1000;

export function indexLateness(days: readonly AttendanceDayRow[]): LatenessIndex {
  const index: LatenessIndex = new Map();
  for (const day of days) {
    index.set(`${day.record_id}|${day.date}`, day);
  }
  return index;
}

/** Вердикт по одному событию журнала. */
export function eventLateness(
  index: LatenessIndex,
  log: AccessLogRecord,
): EventLateness {
  if (!log.record_id) {
    return NONE;
  }
  const day = index.get(
    `${log.record_id}|${isoDate(new Date(log.created_at_millis))}`,
  );
  if (!day || !day.scheduled || day.arrival_time_millis == null) {
    return NONE;
  }
  if (Math.abs(day.arrival_time_millis - log.created_at_millis) > ARRIVAL_TOLERANCE_MS) {
    return NONE;
  }
  if (day.flags.includes('late')) {
    // Знак дельты — забота сервера, а направление уже сказано флагом: день помечен
    // опозданием, значит минуты опоздания это её модуль. Так строка не превратится в
    // «-12 мин опоздания», если однажды сервер начнёт считать дельту от смены.
    return {kind: 'late', minutes: Math.abs(day.arrival_delta_minutes ?? 0)};
  }
  if (day.flags.includes('onTime')) {
    return {kind: 'onTime'};
  }
  return NONE;
}

/**
 * Минуты опоздания для сортировки. `null` — сравнивать нечего, и такие строки уезжают
 * вниз в обе стороны (см. `sortLogs`).
 */
export function lateMinutes(
  index: LatenessIndex,
  log: AccessLogRecord,
): number | null {
  const verdict = eventLateness(index, log);
  if (verdict.kind === 'late') {
    return verdict.minutes;
  }
  // Пришедший вовремя — это ноль опоздания, а не «нет данных»: он обязан стоять в
  // сортировке ниже опоздавших, но выше тех, о ком неизвестно ничего.
  return verdict.kind === 'onTime' ? 0 : null;
}
